import React from 'react';
import PropTypes from 'prop-types';
import { get, intersection, find } from 'lodash';
import { favoriteItem, removeFavoriteItem } from '@bd/bdcore-data/build/Favorites/actions';
import RouteCollection from '@bd/bdcore-named-routes';
import { getPlanRouteProps } from '@bd/bdcore-data/build/Plans/selectors';
import { getHomeRouteProps } from '@bd/bdcore-data/build/Homes/selectors';
import { getCommunityRouteProps } from '@bd/bdcore-data/build/Communities/selectors';
import withData from '@bd/bdcore-data';
import { ConfigStore } from '@bd/bdcore-utilities';

const getItemId = item => get(item, '_id', get(item, 'contentUrl'));

const favoritableProps = {
  home: item => ({
    favoriteId: getItemId(item),
    title: get(item, 'address.streetAddress'),
    location: get(item, 'geoIndexed'),
    relativePath: RouteCollection.getPath('home-detail', getHomeRouteProps(item)),
    image: get(item, `${ConfigStore.getConfig('entity_logos.home')}`),
    type: get(item, 'type'),
    favoritedAt: Date.now(),
  }),
  lot: item => ({
    favoriteId: getItemId(item),
    title: get(item, 'address.streetAddress'),
    location: get(item, 'geoIndexed'),
    image: get(item, `${ConfigStore.getConfig('entity_logos.home')}`),
    type: get(item, 'type'),
    favoritedAt: Date.now(),
  }),
  plan: item => ({
    favoriteId: getItemId(item),
    title: get(item, 'name'),
    relativePath: RouteCollection.getPath('plan-detail', getPlanRouteProps(item)),
    image: get(item, `${ConfigStore.getConfig('entity_logos.plan')}`),
    type: get(item, 'type'),
    favoritedAt: Date.now(),
  }),
  community: item => ({
    favoriteId: getItemId(item),
    title: get(item, 'name'),
    location: get(item, 'geoIndexed'),
    relativePath: RouteCollection.getPath('community-detail', getCommunityRouteProps(item)),
    image: get(item, `${ConfigStore.getConfig('entity_logos.community')}`),
    type: get(item, 'type'),
    favoritedAt: Date.now(),
  }),
  content: item => ({
    ...item,
    element: undefined,
    favoriteId: getItemId(item),
    title: get(item, 'name', 'No Title'),
    image: item.contentUrl || item.imageUrl ? item : 'https://via.placeholder.com/200',
    type: 'content',
    favoritedAt: Date.now(),
  }),
  favorite: () => {},
};

class FavoriteToggle extends React.Component {
  constructor(props) {
    super(props);

    const propKeys = Object.keys(this.props);
    const favoritablePropKeys = Object.keys(favoritableProps);
    const [match] = intersection(propKeys, favoritablePropKeys);

    if (!match) {
      throw Error(`Item cannot be favorited. Supported types are ${favoritablePropKeys.join(', ')}`);
    }

    this.state = {
      match,
      item: this.props[match],
    };
  }

  toggleFavorite = event => {
    const { item, match } = this.state;
    const { dispatch } = this.props;
    event.stopPropagation();
    event.preventDefault();

    if (match === 'favorite') {
      dispatch(removeFavoriteItem(item));
    } else if (this.isActive()) {
      dispatch(removeFavoriteItem(this.findActiveItem()));
    } else {
      dispatch(favoriteItem(favoritableProps[match](item)));
    }
  }

  findActiveItem = () => {
    const { favorites } = this.props;
    const { item } = this.state;

    return find(favorites, { favoriteId: getItemId(item) });
  }

  isActive = () => {
    return this.state.match === 'favorite' || !!this.findActiveItem();
  }

  render() {
    const { children, className } = this.props;

    return (
      <div
        role="button"
        tabIndex={0}
        className={`FavoriteToggle ${this.isActive() ? 'FavoriteToggle-active' : ''} ${className}`}
        onClick={this.toggleFavorite}
        onKeyUp={this.toggleFavorite}
      >
        {children}
      </div>
    );
  }
}

FavoriteToggle.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  dispatch: PropTypes.func.isRequired,
  favorites: PropTypes.arrayOf(PropTypes.object).isRequired,
};

FavoriteToggle.defaultProps = {
  className: undefined,
  children: undefined,
};

export default withData({ favorites: true })(FavoriteToggle);



// WEBPACK FOOTER //
// ./src/bdcoreCandidates/Favorites/FavoriteToggle.js